import { useQuery } from 'react-query';
import axios from 'axios';

interface InvoiceTotalResponse {
  success: boolean;
  data: {
    grandTotal: number;
    breakdown: {
      baseTotal: number;
      afterDiscounts: number;
      afterFees: number;
      taxAmount: number;
      grandTotal: number;
    };
  };
  message: string;
}

interface UseInvoiceTotalResult {
  data: InvoiceTotalResponse['data'] | null;
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
}

/**
 * Hook to fetch canonical invoice total from the new API endpoint
 * @param invoiceId - The invoice ID to fetch total for
 * @returns Object with data, loading state, and error state
 */
export const useInvoiceTotal = (invoiceId: number | null): UseInvoiceTotalResult => {
  const {
    data,
    isLoading,
    isError,
    error
  } = useQuery({
    queryKey: ['invoice-total', invoiceId],
    queryFn: async (): Promise<InvoiceTotalResponse> => {
      if (!invoiceId) {
        throw new Error('Invoice ID is required');
      }
      
      const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/api/v1/tools/invoice/${invoiceId}/total`);
      return response.data;
    },
    enabled: !!invoiceId, // Only fetch if invoiceId is provided
    staleTime: 5 * 60 * 1000, // Consider data fresh for 5 minutes
    cacheTime: 10 * 60 * 1000, // Keep in cache for 10 minutes
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff
  });

  return {
    data: data?.data || null,
    isLoading,
    isError,
    error: error as Error | null
  };
}; 